import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { LoaderCircle } from 'lucide-react'
import { cn } from './util.ts'
import { Kbd } from './Kbd.tsx'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm'

const base =
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium select-none ' +
  'transition duration-fast ease-out active:scale-98 ' +
  'disabled:pointer-events-none disabled:opacity-45'

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-ink shadow-raised hover:bg-accent-hover',
  secondary: 'bg-raised text-ink shadow-raised ring-1 ring-line-strong ring-inset hover:bg-overlay',
  ghost: 'text-ink-muted hover:bg-hover hover:text-ink active:bg-press',
  danger: 'bg-danger-soft text-danger ring-1 ring-danger-line ring-inset hover:bg-danger hover:text-canvas',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-sm',
  md: 'h-9 px-3.5 text-base',
  lg: 'h-11 px-4 text-base',
  icon: 'size-9',
  'icon-sm': 'size-7',
}

/** Class string of a button, for router Links that should look like one. */
export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', extra?: string): string {
  return cn(base, variants[variant], sizes[size], extra)
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: LucideIcon
  iconRight?: LucideIcon
  /** Shortcut shown inside the button, e.g. ['⌘', '↵']. */
  kbd?: string[]
  /** Shows a spinner in place of the icon and disables the button while a request runs. */
  loading?: boolean
  children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon: Icon, iconRight: IconRight, kbd, loading, className, children, type = 'button', disabled, ...rest },
  ref,
) {
  const iconSize = size === 'sm' || size === 'icon-sm' ? 14 : 16
  const Lead = loading ? LoaderCircle : Icon
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClass(variant, size, className)}
      {...rest}
    >
      {Lead && <Lead size={iconSize} strokeWidth={1.75} aria-hidden className={loading ? 'animate-spin' : undefined} />}
      {children}
      {IconRight && <IconRight size={iconSize} strokeWidth={1.75} aria-hidden />}
      {kbd && <Kbd keys={kbd} tone={variant === 'primary' ? 'onAccent' : 'default'} />}
    </button>
  )
})
